import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Pagination, Tag } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Redemption = {
  id: string; createdAt: string; gameUserId: number; nickname: string | null; status: string; commandKey: string;
  giftcode: { code: string; amount: string; moneyType: string };
};
type Page = { items: Redemption[]; total: number; page: number; pageSize: number };

const STATUS: Record<string, [string, 'green' | 'blue' | 'red']> = { DONE: ['Đã cộng', 'green'], PENDING: ['Đang cộng', 'blue'], FAILED: ['Lỗi, không cộng', 'red'] };

const FIELDS: FilterField[] = [
  { key: 'code', label: 'Mã giftcode', kind: 'text' },
  { key: 'userId', label: 'Mã người chơi', kind: 'number' },
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ['DONE', 'Đã cộng'], ['PENDING', 'Đang cộng'], ['FAILED', 'Lỗi']] },
  { key: 'from', label: 'Từ ngày', kind: 'date' },
  { key: 'to', label: 'Đến ngày', kind: 'date' },
];
const KEYS = FIELDS.map((x) => x.key);

const columns: ColumnDef<Redemption, any>[] = [
  { header: 'Lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Mã', id: 'code', accessorFn: (r) => r.giftcode.code, cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Người chơi', id: 'u', accessorFn: (r) => r.nickname ?? '#' + r.gameUserId,
    cell: (c) => <Link className="cell-link" to={`/users/${c.row.original.gameUserId}`}>{c.getValue()}</Link> },
  { header: 'Số tiền', id: 'amt', accessorFn: (r) => r.giftcode.amount,
    cell: (c) => <Money v={c.getValue()} unit={c.row.original.giftcode.moneyType === 'xu' ? 'xu' : true} /> },
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => {
    const [label, type] = STATUS[c.getValue()] ?? [c.getValue(), 'blue'];
    return <Tag size="sm" type={type}>{label}</Tag>;
  } },
  { header: 'Lệnh', accessorKey: 'commandKey', enableSorting: false,
    cell: (c) => <span className="num" title={c.getValue()}>{String(c.getValue()).slice(0, 13)}</span> },
];

export function GiftcodeRedemptionsPage() {
  const f = useUrlFilters(KEYS);
  const q = useQuery({
    queryKey: ['giftcode-redemptions', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/giftcodes/redemptions', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  return (
    <div className="page">
      <PageHeader title="Giftcode đã nhập" lead="Mỗi lần người chơi nhập mã: ai, lúc nào, cộng được chưa. Mỗi dòng dẫn tới lệnh tiền trên hệ A." />
      <FilterBar id="gr" fields={FIELDS} filters={f} exportPath="/api/admin/giftcodes/redemptions" />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} rowKey={(r) => r.id}
        empty={{ title: f.active ? 'Không có lượt nhập khớp bộ lọc' : 'Chưa có ai nhập giftcode', body: f.active ? 'Nới bộ lọc để xem thêm.' : undefined }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} lượt nhập`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
    </div>
  );
}
